import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { storefrontApi } from '../services/api'

interface CartItem {
  id: string
  productId: string
  name: string
  price: number
  quantity: number
  type: string
  category?: string
}

interface CartContextType {
  items: CartItem[]
  cartToken: string | null
  subtotal: number
  discount: number
  total: number
  discountCode: string | null
  loading: boolean
  addToCart: (item: CartItem) => void
  removeFromCart: (id: string) => void
  updateQuantity: (id: string, quantity: number) => void
  applyCode: (code: string) => Promise<{ success: boolean; error?: string }>
  clearCart: () => void
  itemCount: number
}

const CartContext = createContext<CartContextType | null>(null)

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartToken, setCartToken] = useState<string | null>(() =>
    localStorage.getItem('storefront_cart_token')
  )
  const [items, setItems] = useState<CartItem[]>([])
  const [subtotal, setSubtotal] = useState(0)
  const [discount, setDiscount] = useState(0)
  const [total, setTotal] = useState(0)
  const [discountCode, setDiscountCode] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Initialize or restore cart
  useEffect(() => {
    if (cartToken) {
      storefrontApi.getCart(cartToken).then(res => {
        const data = res.data
        setItems(data.items || [])
        setSubtotal(data.subtotal || 0)
        setDiscount(data.discount || 0)
        setTotal(data.total || 0)
        setDiscountCode(data.discountCode || null)
      }).catch(() => {
        // Cart expired, create new one
        localStorage.removeItem('storefront_cart_token')
        setCartToken(null)
      })
    }
  }, [])

  const ensureCart = async (): Promise<string> => {
    if (cartToken) return cartToken
    const res = await storefrontApi.createCart()
    const token = res.data.cartToken
    setCartToken(token)
    localStorage.setItem('storefront_cart_token', token)
    return token
  }

  const syncCart = async (newItems: CartItem[]) => {
    const token = await ensureCart()
    const res = await storefrontApi.updateCart(token, { items: newItems })
    setSubtotal(res.data.subtotal || 0)
    setTotal(res.data.total || 0)
  }

  const addToCart = (item: CartItem) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === item.id)
      let updated: CartItem[]
      if (existing) {
        updated = prev.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i)
      } else {
        updated = [...prev, { ...item, quantity: 1 }]
      }
      syncCart(updated)
      return updated
    })
  }

  const removeFromCart = (id: string) => {
    setItems(prev => {
      const updated = prev.filter(i => i.id !== id)
      syncCart(updated)
      return updated
    })
  }

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) return removeFromCart(id)
    setItems(prev => {
      const updated = prev.map(i => i.id === id ? { ...i, quantity } : i)
      syncCart(updated)
      return updated
    })
  }

  const applyCode = async (code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = await ensureCart()
      const res = await storefrontApi.applyCode(token, code)
      setDiscountCode(res.data.discountCode)
      setDiscount(res.data.discount || 0)
      setTotal(res.data.total || 0)
      setSubtotal(res.data.subtotal || 0)
      return { success: true }
    } catch (err: any) {
      return { success: false, error: err.response?.data?.error || 'Invalid code' }
    }
  }

  const clearCart = () => {
    setItems([])
    setSubtotal(0)
    setDiscount(0)
    setTotal(0)
    setDiscountCode(null)
    localStorage.removeItem('storefront_cart_token')
    setCartToken(null)
  }

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0)

  return (
    <CartContext.Provider value={{
      items, cartToken, subtotal, discount, total, discountCode, loading,
      addToCart, removeFromCart, updateQuantity, applyCode, clearCart, itemCount,
    }}>
      {children}
    </CartContext.Provider>
  )
}
